import type { AtendimentoSession } from './session';
import { atendimentoHomePath, atendimentoMeusPedidosPath, atendimentoPedidoPath } from './session';

// Isolamento do atendimento (PRAT-64). Quem entrou pelo QR Code (ou pela Nova
// Venda do gestor) não pode cair nas telas do web-app — lista de lojas, busca,
// pedidos, perfil, página da loja, checkout de delivery, barra inferior. Isso
// acontece por redirecionamentos do próprio app (fim do login volta para `/`,
// PIX expirado leva para `/:slug`, 401 no meio do caminho), pelo voltar do
// navegador ou por alguém digitando o endereço. Enquanto a aba "está no
// atendimento", essas rotas devolvem para o equivalente dentro dele.
//
// "Está no atendimento" = a sessão do atendimento desta aba (sessionStorage,
// ver session.ts) e uma visita recente a alguma tela `/:slug/atendimento*`.
// Quem nunca entrou no atendimento não tem nenhuma das duas: o web-app segue
// igual para ele.

export const VISITA_ATENDIMENTO_KEY = 'atendimento-visita';

// Depois disso sem voltar ao atendimento, a aba volta a ser do web-app.
export const VALIDADE_VISITA_MS = 3 * 60 * 60 * 1000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function registrarVisitaAtendimento(
  storage: StorageLike | null = defaultStorage(),
  agora = Date.now()
) {
  try {
    storage?.setItem(VISITA_ATENDIMENTO_KEY, String(agora));
  } catch {
    // sem persistência: o isolamento só não sobrevive a recarregar
  }
}

export function lerVisitaAtendimento(
  storage: StorageLike | null = defaultStorage()
): number | null {
  try {
    const valor = Number(storage?.getItem(VISITA_ATENDIMENTO_KEY));
    return Number.isFinite(valor) && valor > 0 ? valor : null;
  } catch {
    return null;
  }
}

// Telas que o atendimento usa ou que não são do web-app: login do cliente
// (volta sozinho para o atendimento) e páginas técnicas.
const LIVRES = [/^\/auth(\/|$)/, /^\/login-by-link$/, /^\/entrar(\/|$)/, /^\/sentry-test$/, /^\/error-boundary-test$/];

// Rotas do web-app que não levam o slug da loja.
const GLOBAIS = new Set(['search', 'profile', 'indicacao', 'cart', 'orders']);

function decodificar(segmento: string): string {
  try {
    return decodeURIComponent(segmento);
  } catch {
    return segmento;
  }
}

// Para onde mandar quem está no atendimento e abriu `pathname`. null = pode
// ficar (tela do atendimento, login, ou outra loja do web-app).
export function destinoIsolamento(
  pathname: string,
  session: AtendimentoSession | null,
  visitaEm: number | null,
  agora = Date.now()
): string | null {
  if (!session || visitaEm == null || agora - visitaEm > VALIDADE_VISITA_MS) return null;

  const path = pathname.replace(/\/+$/, '') || '/';
  if (LIVRES.some(regra => regra.test(path))) return null;

  const [primeiro, segundo] = path.split('/').filter(Boolean).map(decodificar);
  // Qualquer tela do atendimento (desta ou de outra loja).
  if (segundo === 'atendimento') return null;

  const { slug, modo } = session;
  const home = atendimentoHomePath(slug);

  if (primeiro === 'orders') {
    if (modo !== 'cliente') return home;
    return segundo ? atendimentoPedidoPath(slug, segundo) : atendimentoMeusPedidosPath(slug);
  }
  if (primeiro === undefined || GLOBAIS.has(primeiro)) return home;
  // A loja do atendimento no web-app (`/:slug`, `/:slug/order`, clube...).
  if (primeiro === slug) return home;

  return null;
}
